/**
 * Rive layer for the Football (Goal Shooting) benchmark port.
 *
 * Loads THREE artboards from the SINGLE `football.riv` file onto three stacked
 * canvases — mirroring the three `RiveWidgetController`s in
 *   packages/amgfootball_v2/lib/presentation/rive/rive_controller_manager.dart:
 *
 *     main    → football.riv  artboard "Main"          (cards, ball, result, history, text)
 *     betting → football.riv  artboard "BettingLayer"  (goal click zones → evtBetClick{betID})
 *     chips   → football.riv  artboard "ChipsLayer"    (chip picker → evtClickChips{chipsAmt})
 *
 * ALL THREE artboards share the state-machine name `Main` — instantiate the
 * SM per artboard, never globally.
 *
 * Why one Rive instance per canvas: `@rive-app/webgl2` clears the canvas at
 * the start of every frame, so two instances sharing a canvas overwrite each
 * other.
 *
 * Input names are the GROUND-TRUTH names from binary-introspecting football.riv
 * (see riveInputs.ts) — the Flutter `rive_inputs.dart` constants have drifted
 * and silently no-op. Nested inputs (Card@CardLeft, History@History1) go
 * through `setNumberStateAtPath`.
 */
import {
  Rive,
  Layout,
  Fit,
  Alignment,
  EventType,
  StateMachineInputType,
} from '@rive-app/webgl2';

import { debugLog, warn } from '../util/logger';
import {
  MainInputs,
  ChipsInputs,
  TextRuns,
  BET_TEXT_RUN,
  ODDS_TEXT_RUN,
  type RiveSide,
} from './riveInputs';

type AnyInput =
  | { type: 'number'; name: string; set(v: number): void; get(): number }
  | { type: 'boolean'; name: string; set(v: boolean): void; get(): boolean }
  | { type: 'trigger'; name: string; fire(): void };

export type TargetKey = 'main' | 'betting' | 'chips';

interface RiveTarget {
  key: TargetKey;
  file: string;
  artboard: string;
  stateMachine: string;
  instance: Rive;
  inputs: Map<string, AnyInput>;
}

const FILES: Record<TargetKey, string> = {
  main: 'football.riv',
  betting: 'football.riv',
  chips: 'football.riv',
};

const ARTBOARDS: Record<TargetKey, string> = {
  main: 'Main',
  betting: 'BettingLayer',
  chips: 'ChipsLayer',
};

const STATE_MACHINES: Record<TargetKey, string> = {
  main: 'Main',
  betting: 'Main',
  chips: 'Main',
};

export interface RiveLayerOptions {
  canvases: Record<TargetKey, HTMLCanvasElement>;
  assetBase?: string;
}

export type RiveEventTopic =
  | 'ready'
  | 'evtBetClick'
  | 'evtClickChips'
  | 'placeBetClick'
  | 'UIButtonClick'
  | string;

type RiveEventCallback = (data: Record<string, unknown>) => void;

export class RiveLayer {
  private readonly canvases: Record<TargetKey, HTMLCanvasElement>;
  private readonly assetBase: string;
  private readonly targets: Map<TargetKey, RiveTarget> = new Map();
  private readonly listeners: Map<string, Set<RiveEventCallback>> = new Map();

  constructor(opts: RiveLayerOptions) {
    this.canvases = opts.canvases;
    this.assetBase = opts.assetBase ?? '/assets/Rive/';
  }

  async init(): Promise<void> {
    this.fitAllCanvases();
    window.addEventListener('resize', () => this.fitAllCanvases());

    await Promise.all([
      this.loadOne('main'),
      this.loadOne('betting'),
      this.loadOne('chips'),
    ]);

    this.dumpInputs();
    this.emit('ready', {});
  }

  private fitAllCanvases(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    for (const canvas of Object.values(this.canvases)) {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) continue;
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
    }
    for (const t of this.targets.values()) {
      try {
        t.instance.resizeDrawingSurfaceToCanvas();
      } catch {
        /* ignore */
      }
    }
  }

  private loadOne(key: TargetKey): Promise<void> {
    return new Promise((resolve, reject) => {
      const file = FILES[key];
      const artboard = ARTBOARDS[key];
      const sm = STATE_MACHINES[key];
      const canvas = this.canvases[key];
      const src = `${this.assetBase}${file}`;
      const rive = new Rive({
        canvas,
        src,
        artboard,
        stateMachines: sm,
        autoplay: true,
        layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
        onLoad: () => {
          try {
            const inputs = this.cacheInputs(rive, sm);
            this.targets.set(key, {
              key,
              file,
              artboard,
              stateMachine: sm,
              instance: rive,
              inputs,
            });
            this.attachEventBridge(rive, key);
            try {
              rive.resizeDrawingSurfaceToCanvas();
            } catch {
              /* ignore */
            }
            resolve();
          } catch (e) {
            reject(e);
          }
        },
        onLoadError: (err: unknown) => {
          reject(
            new Error(`Failed to load ${src} [${artboard}/${sm}]: ${String(err)}`),
          );
        },
      });
    });
  }

  private cacheInputs(rive: Rive, sm: string): Map<string, AnyInput> {
    const out = new Map<string, AnyInput>();
    let list: unknown[] = [];
    try {
      list = rive.stateMachineInputs(sm) ?? [];
    } catch (e) {
      warn('[Rive] stateMachineInputs failed for', sm, e);
      return out;
    }
    for (const raw of list) {
      const inp = raw as {
        name: string;
        type: number;
        value: number | boolean;
        fire(): void;
      };
      switch (inp.type) {
        case StateMachineInputType.Number:
          out.set(inp.name, {
            type: 'number',
            name: inp.name,
            set: (v) => {
              inp.value = v;
            },
            get: () => Number(inp.value ?? 0),
          });
          break;
        case StateMachineInputType.Boolean:
          out.set(inp.name, {
            type: 'boolean',
            name: inp.name,
            set: (v) => {
              inp.value = v;
            },
            get: () => Boolean(inp.value),
          });
          break;
        case StateMachineInputType.Trigger:
          out.set(inp.name, {
            type: 'trigger',
            name: inp.name,
            fire: () => inp.fire(),
          });
          break;
        default:
          debugLog('[Rive] unknown input type', inp.type, 'for', inp.name);
      }
    }
    return out;
  }

  /** Dump every loaded artboard's inputs to the console for debugging. */
  private dumpInputs(): void {
    for (const t of this.targets.values()) {
      const rows = Array.from(t.inputs.values()).map((i) => ({
        target: t.key,
        artboard: t.artboard,
        sm: t.stateMachine,
        name: i.name,
        type: i.type,
      }));
      // eslint-disable-next-line no-console
      console.groupCollapsed(
        `[Rive] ${t.key} (${t.artboard} / ${t.stateMachine}) — ${rows.length} input(s)`,
      );
      // eslint-disable-next-line no-console
      console.table(rows);
      // eslint-disable-next-line no-console
      console.groupEnd();
    }
  }

  private attachEventBridge(rive: Rive, target: TargetKey): void {
    try {
      rive.on(EventType.RiveEvent, (event: unknown) => {
        const e = event as {
          data?: { name?: string; properties?: Record<string, unknown> };
        };
        const name = e?.data?.name;
        if (!name) return;
        const props = { ...(e?.data?.properties ?? {}), __target: target };
        if (this.listeners.has(name)) {
          this.emit(name, props);
        } else {
          debugLog('[Rive][event:passthrough]', target, name, props);
        }
      });
    } catch (e) {
      warn('[Rive] event bridge attach failed', e);
    }
  }

  // =================================================================
  // Public high-level API — mirrors RiveControllerManager
  // =================================================================

  /** Per-goal card value (nested `Card` input). 1..52-ish. */
  setLeftCard(card: number): void {
    this.setNumberAtPath('main', MainInputs.card, card, MainInputs.cardPathLeft);
  }
  setRightCard(card: number): void {
    this.setNumberAtPath('main', MainInputs.card, card, MainInputs.cardPathRight);
  }
  setCenterCard(card: number): void {
    this.setNumberAtPath('main', MainInputs.card, card, MainInputs.cardPathCenter);
  }

  /**
   * Card-reveal animation — top-level `Card` input on the Main artboard.
   * Values: 0=reset, 1=reveal left, 2=reveal right, 3=start reveal center,
   * 4=finish center reveal.
   */
  setCardAnim(value: 0 | 1 | 2 | 3 | 4): void {
    this.setNumberAny('main', MainInputs.cardAnim, value);
  }

  /** Ball-shoot trajectory. 0=clear, 1=armed, 2..9=directional shot. */
  setBallShoot(value: number): void {
    this.setNumberAny('main', MainInputs.ballShoot, value);
  }

  /** Win/lose banner. 0=reset, 1=win, 2=lose. */
  setWinLoseResult(value: 0 | 1 | 2): void {
    this.setNumberAny('main', MainInputs.result, value);
  }

  /** Bet window. 1=start bet, 2=no-more-bet. */
  setStartEndBet(value: 1 | 2): void {
    this.setNumberAny('main', MainInputs.startStopBet, value);
  }

  /**
   * History strip — 10 nested slots `History`@`History1..History10`.
   * Values 0=empty, 1/2/3 = left/center/right goal (newest first).
   */
  updateHistory(values: ReadonlyArray<0 | 1 | 2 | 3>): void {
    for (let i = 0; i < 10; i++) {
      const v = (i < values.length ? values[i] : 0) as number;
      this.setNumberAtPath('main', MainInputs.history, v, `History${i + 1}`);
    }
  }

  /** Chip selection highlight on the chips artboard (1-based index). */
  setChipSelect(index1Based: number): void {
    if (this.setNumberAny('chips', ChipsInputs.chipSelect, index1Based)) return;
    for (const alt of ChipsInputs.chipSelectFallbacks) {
      if (this.setNumberAny('chips', alt, index1Based)) return;
    }
  }

  setIsMobile(value: boolean): void {
    this.setBoolAny('betting', MainInputs.isMobile, value);
    this.setBoolAny('main', MainInputs.isMobile, value);
  }

  setSoundOn(value: boolean): void {
    // Real binary name is a single shared `IsSoundOn`; the Flutter drifted
    // names are tried as fallbacks per artboard.
    for (const key of ['main', 'betting', 'chips'] as const) {
      if (!this.setBoolAny(key, MainInputs.isSoundOn, value)) {
        this.setBoolAny(key, 'isSoundOn', value);
        this.setBoolAny(key, 'isSoundOnBet', value);
        this.setBoolAny(key, 'isSoundOnChips', value);
      }
    }
  }

  // -------- Text runs (Main artboard unless noted) --------

  setBalanceText(value: string): void {
    this.setTextCandidates('main', TextRuns.balance, value);
  }
  setUsernameText(value: string): void {
    this.setTextCandidates('main', TextRuns.username, value);
  }
  setRoundText(value: string): void {
    this.setTextCandidates('main', TextRuns.round, value);
  }
  setTotalBetText(value: string): void {
    this.setTextCandidates('main', TextRuns.totalBet, value);
  }
  setAreaBetText(side: RiveSide, value: string): void {
    this.setTextCandidates('main', BET_TEXT_RUN[side], value);
  }
  /** Odds text is written with a literal `X` prefix (e.g. "X2.00"). */
  setOddsText(side: RiveSide, odds: string): void {
    this.setTextCandidates('main', ODDS_TEXT_RUN[side], `X${odds}`);
  }
  setWinAmountText(value: string): void {
    this.setTextCandidates('main', TextRuns.youWinAmount, value);
  }
  /** Chip denomination text on the chips artboard (1-based index). */
  setChipText(index1Based: number, value: string): void {
    this.setTextOne('chips', `chip${index1Based}Amount`, value);
  }

  // =================================================================
  // Pub/sub
  // =================================================================

  on(topic: RiveEventTopic, cb: RiveEventCallback): () => void {
    let set = this.listeners.get(topic);
    if (!set) {
      set = new Set();
      this.listeners.set(topic, set);
    }
    set.add(cb);
    return () => set!.delete(cb);
  }

  private emit(topic: string, data: Record<string, unknown>): void {
    const set = this.listeners.get(topic);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(data);
      } catch (e) {
        warn('[Rive listener]', e);
      }
    }
  }

  // =================================================================
  // Low-level input plumbing
  // =================================================================

  private setNumberAny(key: TargetKey, name: string, value: number): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(name);
    if (inp?.type === 'number') {
      inp.set(value);
      return true;
    }
    if (inp) {
      debugLog(`[Rive] '${name}' on ${key} is ${inp.type}, not number`);
      return false;
    }
    debugLog(`[Rive] number '${name}' not found on ${key} (${t.artboard})`);
    return false;
  }

  private setBoolAny(key: TargetKey, name: string, value: boolean): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(name);
    if (inp?.type === 'boolean') {
      inp.set(value);
      return true;
    }
    return false;
  }

  /** Nested-input setter (Rive WebGL2 "at path" API). */
  private setNumberAtPath(
    key: TargetKey,
    inputName: string,
    value: number,
    path: string,
  ): void {
    const t = this.targets.get(key);
    if (!t) return;
    try {
      t.instance.setNumberStateAtPath(inputName, value, path);
    } catch (e) {
      debugLog(
        `[Rive] setNumberStateAtPath('${inputName}', ${value}, '${path}') failed on ${key}:`,
        e,
      );
    }
  }

  private setTextOne(key: TargetKey, name: string, value: string): void {
    const t = this.targets.get(key);
    if (!t) return;
    try {
      t.instance.setTextRunValue(name, value);
    } catch {
      /* not present on this artboard */
    }
  }

  private setTextCandidates(
    key: TargetKey,
    names: readonly string[],
    value: string,
  ): void {
    for (const n of names) this.setTextOne(key, n, value);
  }
}
